// Fleetspoke Assets brand: logo concept A "Spoke A" (appstore_submission/logo-concepts/concept-a-spoke-a.svg) and
// its lockup (mark + FLEETSPOKE / ASSETS wordmark in Montserrat). public/favicon.svg carries the same mark.
import '@fontsource/montserrat/latin-600.css';
import '@fontsource/montserrat/latin-700.css';
import '@fontsource/montserrat/latin-800.css';
import { Box, type SxProps, type Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useId } from 'react';

export const BRAND_FONT =
  'Montserrat, "Noto Sans SC", "Segoe UI", "Helvetica Neue", Arial, sans-serif';
export const BRAND_NAME = 'Fleetspoke Assets';

type Tone = 'light' | 'dark';

// the wordmark colours of the approved lockups (light ground / dark ground)
const WORDMARK: Record<Tone, { fleetspoke: string; assets: string; spoke: string }> = {
  light: { fleetspoke: '#2463A6', assets: '#1C2630', spoke: '#2470B5' },
  dark: { fleetspoke: '#7DBDEB', assets: '#F0A248', spoke: '#5AAEE8' },
};

// viewBox cropped to the drawing (the source file is a 1024 square with padding): width / height = 622 / 730
const VIEWBOX = '172 146 622 730';
const ASPECT = 622 / 730;

/** The mark alone. `size` is its height in px; decorative unless `title` is given. */
export function BrandMark({
  size,
  tone,
  title,
  sx,
}: {
  size: number;
  tone?: Tone;
  title?: string;
  sx?: SxProps<Theme>;
}) {
  const theme = useTheme();
  const colours = WORDMARK[tone ?? theme.palette.mode];
  const id = useId().replace(/[^\w-]/g, '');
  return (
    <Box
      component="svg"
      viewBox={VIEWBOX}
      width={Math.round(size * ASPECT)}
      height={size}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      sx={{ display: 'block', flex: 'none', ...sx }}
    >
      <defs>
        <linearGradient id={`${id}b`} x1="1" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1D8FD3" />
          <stop offset="1" stopColor="#245FA3" />
        </linearGradient>
        <linearGradient id={`${id}a`} x1="1" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F4A844" />
          <stop offset="0.55" stopColor="#D97A12" />
          <stop offset="1" stopColor="#B85F08" />
        </linearGradient>
      </defs>
      <g stroke={colours.spoke} strokeWidth="13" strokeLinecap="round">
        <line x1="332" y1="468" x2="236" y2="398" />
        <line x1="332" y1="468" x2="206" y2="478" />
        <line x1="332" y1="468" x2="258" y2="556" />
      </g>
      <g fill={colours.spoke}>
        <circle cx="236" cy="398" r="29" />
        <circle cx="206" cy="478" r="29" />
        <circle cx="258" cy="556" r="29" />
      </g>
      <path
        fill={`url(#${id}b)`}
        d="M790 150 L790 262 L650 262 C480 262 382 362 370 530 C362 650 358 760 354 872 C338 752 318 620 318 506 C318 296 468 150 650 150 Z"
      />
      <path
        fill={`url(#${id}a)`}
        fillRule="evenodd"
        d="M400 872 L400 566 C400 404 494 294 656 294 L790 294 L790 872 L666 872 L666 726 L524 726 L524 872 Z M524 612 L666 612 L666 410 L656 410 C574 410 524 464 524 546 Z"
      />
    </Box>
  );
}

// proportions follow the approved lockup (mark 58 : FLEETSPOKE 10.5 : ASSETS 24), with a readable floor at small sizes
const SIZES = {
  sm: { mark: 36, fleetspoke: 8.5, assets: 17, gap: 1.25, lead: 0.375 },
  md: { mark: 52, fleetspoke: 10, assets: 22, gap: 1.5, lead: 0.5 },
  lg: { mark: 76, fleetspoke: 13.5, assets: 31, gap: 2, lead: 0.75 },
} as const;

/** Mark + wordmark. The brand name is not translated; the whole lockup reads as one image named BRAND_NAME. */
export function BrandLockup({
  size = 'md',
  tone,
  markOnlyOnPhone = false,
  sx,
}: {
  size?: keyof typeof SIZES;
  tone?: Tone;
  /** drop the wordmark below the `sm` breakpoint (a crowded app bar) */
  markOnlyOnPhone?: boolean;
  sx?: SxProps<Theme>;
}) {
  const theme = useTheme();
  const t = tone ?? theme.palette.mode;
  const s = SIZES[size];
  return (
    <Box
      role="img"
      aria-label={BRAND_NAME}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: s.gap, ...sx }}
    >
      <BrandMark size={s.mark} tone={t} />
      <Box
        aria-hidden
        sx={{
          display: markOnlyOnPhone ? { xs: 'none', sm: 'grid' } : 'grid',
          justifyItems: 'start',
          fontFamily: BRAND_FONT,
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: s.fleetspoke,
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: '0.3em',
            mr: '-0.3em', // tracking adds space after the last letter too
            color: WORDMARK[t].fleetspoke,
          }}
        >
          FLEETSPOKE
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: s.assets,
            fontWeight: 800,
            lineHeight: 1.05,
            letterSpacing: '0.06em',
            mt: s.lead,
            color: WORDMARK[t].assets,
          }}
        >
          ASSETS
        </Box>
      </Box>
    </Box>
  );
}

/** Full-screen brand splash while the session loads (continues the boot screen in index.html). */
export function BrandSplash() {
  return (
    <Box
      role="status"
      aria-label={BRAND_NAME}
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'background.default',
      }}
    >
      <BrandMark
        size={64}
        sx={{
          animation: 'brand-pulse 1.4s ease-in-out infinite',
          '@keyframes brand-pulse': { '50%': { opacity: 0.45 } },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
    </Box>
  );
}
